import { useId, type FormEvent } from "react";
import { Button } from "../components/Button";
import { ErrorMessage } from "../components/ErrorMessage";
import { SearchClearButton } from "../components/SearchClearButton";
import { CATALOG_SORT_OPTIONS } from "./catalog-filters";
import { useCatalog } from "./CatalogContext";
import { GridDensityToggle } from "../ui/GridDensityToggle";

export function CatalogToolbar() {
  const catalog = useCatalog();
  const formId = useId();
  const years = catalog.years;

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    catalog.commitQuery();
  }

  const canReset =
    catalog.filters.query !== "" ||
    catalog.filters.year !== "" ||
    catalog.filters.sort !== "collector_asc" ||
    catalog.filters.rareOnly ||
    catalog.draftQuery.trim() !== "";

  return (
    <form className="hw-catalog-toolbar" onSubmit={onSubmit} role="search">
      <div className="hw-catalog-toolbar__search">
        <label className="hw-visually-hidden" htmlFor={`${formId}-q`}>
          Поиск
        </label>
        <div className="hw-search-field">
          <input
            id={`${formId}-q`}
            className="hw-field__input hw-catalog-toolbar__input"
            name="q"
            value={catalog.draftQuery}
            onChange={(event) => catalog.setDraftQuery(event.target.value)}
            placeholder="Название, № или набор"
            autoComplete="off"
            enterKeyHint="search"
          />
          <SearchClearButton
            value={catalog.draftQuery}
            onClear={() => catalog.commitQuery("")}
          />
        </div>
        <Button type="submit" className="hw-catalog-toolbar__submit">
          Найти
        </Button>
        {canReset ? (
          <Button
            type="button"
            variant="ghost"
            className="hw-catalog-toolbar__reset"
            onClick={() => catalog.resetFilters()}
          >
            Сброс
          </Button>
        ) : null}
      </div>

      <div className="hw-catalog-toolbar__filters">
        <label className="hw-select-field" htmlFor={`${formId}-year`}>
          <span className="hw-visually-hidden">Год</span>
          <select
            id={`${formId}-year`}
            className="hw-select"
            value={catalog.filters.year}
            onChange={(event) => catalog.setYear(event.target.value)}
            aria-invalid={
              catalog.filters.year !== "" && !/^\d{4}$/.test(catalog.filters.year)
            }
          >
            <option value="">Все годы</option>
            {years.map((year) => (
              <option key={year} value={String(year)}>
                {year}
              </option>
            ))}
          </select>
        </label>

        <label className="hw-select-field hw-select-field--sort" htmlFor={`${formId}-sort`}>
          <span className="hw-visually-hidden">Сортировка</span>
          <select
            id={`${formId}-sort`}
            className="hw-select"
            value={catalog.filters.sort}
            title={
              CATALOG_SORT_OPTIONS.find((option) => option.value === catalog.filters.sort)
                ?.description
            }
            onChange={(event) =>
              catalog.setSort(event.target.value as (typeof CATALOG_SORT_OPTIONS)[number]["value"])
            }
          >
            {CATALOG_SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value} title={option.description}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          className={
            catalog.filters.rareOnly
              ? "hw-chip hw-chip--active"
              : "hw-chip"
          }
          aria-pressed={catalog.filters.rareOnly}
          onClick={() => catalog.setRareOnly(!catalog.filters.rareOnly)}
        >
          ★ Редкие
        </button>
        <GridDensityToggle />
      </div>

      {catalog.filters.year !== "" && !/^\d{4}$/.test(catalog.filters.year) ? (
        <ErrorMessage id={`${formId}-year-error`}>
          Укажите год четырьмя цифрами или выберите «Все годы».
        </ErrorMessage>
      ) : null}
    </form>
  );
}
